// В.1.c-2-fix2: единая «чистая» (без DOM) функция, которая определяет,
// нужен ли picker при размещении карты, какой именно, и какие opts надо
// зафиксировать, если picker не нужен.
//
// Возвращает один из:
//   { kind: 'prompt', prompt: { type, ... }, opts: {...}? }
//   { kind: 'opts',   opts: {...} }
//   { kind: 'none' }
//
// Используется UI-кодом в main.js и UI-флоу-тестами в tests/v1c-2-fix2-test.mjs
// для воспроизведения симптома юзера: до fix2 picker не открывался для карт
// Глобала, потому что атак-priority short-circuit пропускал ветку placement-prompt.

export function determinePlacementPrompt(state, ownerIdx, card, line) {
  const me = state.players[ownerIdx];

  // Аудитор Деуса 1-й линии — picker возврата.
  if (card.id === 'deus_auditor_banka' && line === 0) {
    return { kind: 'prompt', prompt: { type: 'auditor1', cardInstanceId: card.instanceId } };
  }
  // Совет акционеров — picker сброса.
  if (card.id === 'deus_sovet_aktsionerov') {
    return { kind: 'prompt', prompt: { type: 'sovet', cardInstanceId: card.instanceId } };
  }

  // БУГ.7: legacy ветки для glb_ofitser_svyazi и glb_netranner_adyutant удалены.
  // Этих ID нет в src/data/cards-global.js (миграция C.1 на v6), ветки никогда
  // не срабатывали.

  // В.2.d-2: Опытный хирург — picker юнита из своего сброса.
  // Если в сбросе нет юнитов ИЛИ нет свободных слотов на поле — Приказ
  // заведомо сгорает (фиксируем opts={resurrectInstanceId:null}, overlay
  // не открывается).
  if (card.id === 'liv_opytnyy_hirurg') {
    const candidates = me.discard.filter(c => c?.type === 'unit');
    if (candidates.length === 0 || !me.hasFreeSlot()) {
      return { kind: 'opts', opts: { resurrectInstanceId: null } };
    }
    return {
      kind: 'prompt',
      prompt: { type: 'hirurg-discard', cardInstanceId: card.instanceId }
    };
  }
  // В.2.d-2: Медик-штурмовик — multi-select picker «до 2 своих юнитов».
  // Если других своих юнитов на поле нет — overlay всё равно открывается,
  // OK на 0 жертв = пропуск (см. медик_штурмовик.js).
  if (card.id === 'liv_medik_shturmovik') {
    return {
      kind: 'prompt',
      prompt: { type: 'shturmovik-multi', cardInstanceId: card.instanceId }
    };
  }
  // В.2.d-1: Дрон-дефибриллятор — picker «какому своему юниту дать Иммунитет».
  // Кандидаты — свои юниты на поле; сам Дрон в этот момент ещё в pendingPlacements,
  // на поле его пока нет — фильтр по instanceId страхует от случая, когда он уже
  // успел оказаться на поле (например, через тест-харнес).
  if (card.id === 'liv_dron_defibrillyator') {
    const candidates = me.unitsOnField().filter(u =>
      u.type === 'unit' && u.instanceId !== card.instanceId
    );
    if (candidates.length === 0) {
      return { kind: 'opts', opts: { immuneTargetInstanceId: null } };
    }
    return {
      kind: 'prompt',
      prompt: { type: 'defibrillyator', cardInstanceId: card.instanceId }
    };
  }

  // БУГ.7: legacy ветка для glb_saper удалена (ID не в v6 cards-global.js).

  // БУГ.2: Сапёр Оникс (C.1 v6) — picker «какому вражескому юниту -1 max armor».
  // order_target='enemy_choose', effect='armor_break:1'. opts.target = выбранный.
  if (card.id === 'global_saper_onix') {
    const enemy = state.players[1 - ownerIdx];
    const candidates = enemy.unitsOnField().filter(u => u && u.type === 'unit');
    if (candidates.length === 0) {
      // Нет вражеских юнитов — Приказ сгорает без target.
      return { kind: 'opts', opts: { target: null } };
    }
    return {
      kind: 'prompt',
      prompt: {
        type: 'enemy-unit-target',
        cardInstanceId: card.instanceId,
        cardId: card.id,
        // Описание для UI — какие юниты выбирать.
        scope: 'enemy_choose',
        // Для логирования / hint в UI.
        purpose: 'armor_break_1'
      }
    };
  }

  // QA-фикс: Наёмник (deus_naemnik) — targeted Order return_to_hand_enemy.
  // Раньше ветки не было → пикер не открывался → Приказ запускался без
  // opts.target → молча invalid_target («карта просто поставилась»). Цель —
  // вражеский юнит с атакой ≤ 2 (по тексту карты). atkMax=2 пробрасывается в
  // UI, чтобы пикер показывал только подходящих врагов.
  if (card.id === 'deus_naemnik') {
    const enemy = state.players[1 - ownerIdx];
    const candidates = enemy.unitsOnField().filter(u =>
      u && u.type === 'unit' && (u.current?.atk ?? u.atk ?? 0) <= 2
    );
    if (candidates.length === 0) {
      // Нет вражеских юнитов с atk ≤ 2 — Приказ сгорает без цели.
      return { kind: 'opts', opts: { target: null } };
    }
    return {
      kind: 'prompt',
      prompt: {
        type: 'enemy-unit-target',
        cardInstanceId: card.instanceId,
        cardId: card.id,
        scope: 'enemy_choose',
        atkMax: 2,
        purpose: 'return_to_hand_atk_le_2'
      }
    };
  }

  // Фиксер Синтетика. Аудит-фикс под v6: перенос Мода на ЛЮБОЙ линии
  // (старого «движения юнита на 2-й линии» больше нет — нет в тексте карты).
  if (card.id === 'syn_fixer') {
    const hasSource = me.unitsOnField().some(u => (u.modifications?.length ?? 0) > 0);
    if (!hasSource) return { kind: 'none' };
    return {
      kind: 'prompt',
      prompt: { type: 'fikser', cardInstanceId: card.instanceId, line, step: 'source' }
    };
  }

  return { kind: 'none' };
}

// Кандидаты для picker'а Молчания у Нетраннера-адъютанта:
// все живые юниты на поле обоих сторон, кроме Героев и самого Нетраннера
// (он на момент picker'а ещё в pendingPlacements, фильтр по instanceId
// страхует от случая, когда он уже на поле).
export function collectNetrannerSilenceCandidates(state, netrannerCard) {
  const out = [];
  for (const p of state.players) {
    for (const u of p.unitsOnField()) {
      if (u.type !== 'unit') continue;
      if (netrannerCard && u.instanceId === netrannerCard.instanceId) continue;
      out.push(u);
    }
  }
  return out;
}
